/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Button, Stack } from "@mui/material";
import { useSortableComponentList } from "cms/components/hooks/use-sortable-component-list";
import { SVGIcons } from "cms/icons/svg-icons";
import { AddLineWithIcon } from "components/add-line-with-icon";
import PositionButtons from "components/position-buttons";
import RemoveIcon from "components/remove-icon";
import { SortableContent } from "components/sortable-content";
import { useTranslation } from "react-i18next";
import { isArrayNotEmpty } from "utils/empty-exists";
import { ComponentDocListItemContext } from "./component-document-list-context";
import { Document } from "cms/components/components/document-list/edit/document";

const defaultDocument = Object.freeze({
    document: "",
    title: ""
});

/**
 * @param {object} props
 * @param {SimplicityDocument[]} props.list
 * @param {() => void} props.onChangeList
 * @param {import('react').ReactNode} props.children
 */
const DocumentListComponentEditWrapper = (props) => {
    const {
        list: initialDocs = [],
        onChangeList,
        defaultImpl,
        // children
    } = props;

    const { t } = useTranslation();

    // FIXME: React render phases must always be pure: https://react.dev/learn/keeping-components-pure
    if (typeof defaultImpl !== "object") {
        throw Error("defaultImpl was not supplied, or is not an object");
    }
    if (typeof onChangeList !== "function") {
        throw Error("onChangeList was not supplied, or is not a function");
    }

    const {
        list,
        sortableMethods,
        controls,
    } = useSortableComponentList(
        initialDocs,
        defaultDocument,
        onChangeList
    );

    const {
        addNewItem,
        addNewItemBelow,
        removeItem,
        updateItems
    } = controls;

    return (
        <>
            <div style={{ padding: "12px 0 24px 0" }}>
                <Button
                    size='medium'
                    variant='contained'
                    color={"primary"}
                    target='warning'
                    onClick={addNewItem}
                >
                    <SVGIcons.common.Create
                        style={{ paddingRight: 4, cursor: "pointer" }}
                    />
                    {t("commons.addListItem")}
                </Button>
            </div>
            <div className={isArrayNotEmpty(list) ? "simplicity-image-list" : undefined}>
                <Stack direction='column'>
                    {isArrayNotEmpty(list) &&
                        list.map((item, index) => {
                            
                            const { guid } = item;

                            const componentDocListItemContext = {
                                list,
                                index,
                                guid,
                                item,
                                position: index,
                                updateItems,
                                addNewItem,
                                removeItem,
                                defaultImpl,
                            };

                            return (
                                <ComponentDocListItemContext.Provider
                                    key={`documents-${guid}`}
                                    value={componentDocListItemContext}
                                >
                                    <SortableContent
                                        id={guid}
                                        index={index}
                                        sortableMethods={sortableMethods}
                                    >
                                        {/*{children}*/}
                                        <Document />

                                        <div className='position-container'>
                                            <PositionButtons
                                                index={index}
                                                list={list}
                                                handleUpdate={onChangeList}
                                            />
                                            <RemoveIcon
                                                className='simplicity-composer-field-editor-remove'
                                                onDeleteItem={removeItem}
                                                index={index}
                                            />
                                        </div>

                                    </SortableContent>
                                    
                                    <AddLineWithIcon
                                        addNewItem={() => addNewItemBelow(index)}
                                    />
                                    
                                </ComponentDocListItemContext.Provider>
                            );
                        })}
                </Stack>
            </div>
        </>
    );
};
DocumentListComponentEditWrapper.displayName = "DocumentListComponentEditWrapper";
export { DocumentListComponentEditWrapper };

/**
 * @typedef {object} SimplicityDocument
 * @property {string} guid
 * @property {string} document
 * @property {string} title
 * @property {number} position
 */
